import { ReactNode, useState } from 'react';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import { CustomAlert, PopupNavigation, Text } from '../../../../../shared/uiComponents';
import { BehaviorReductionProperties } from '../../../../../redux/API/ClientAPIHelpers/behaviorReductionProperties';
import {
	BrainIcon,
	BullsEyeArrowIcon,
	CalendarDayIcon,
	CircleIcon,
	ClapperBoardIcon,
	DatabaseIcon,
	GearIcon,
	InfoSvg,
} from '../../../../../shared/Assets/svgIcons';
import EditBehaviorDetails from './editBehaviorDetails';
import GraphBehavior from './BehaviorReductionGraph/graphBehavior';
import DeleteBehavior from './deleteBehavior';
import ArchiveComponent from './archive';
import UnarchiveComponent from './unarchive';
import { TEXT } from '../../../../../shared/Helpers/colors';

interface DetailsProps {
	allBehaviors?: BehaviorReductionProperties[];
	currentIndex?: number;

	data: BehaviorReductionProperties;
}

export const DetailsContent = ({ data }: { data: BehaviorReductionProperties }) => {
	const { clientId } = useParams();

	const { id, name, goalName, definition, category, recordingType, createdAt, dataToday, isArchived } = data;

	if (!clientId) return null;

	const renderRow = (icon: ReactNode, key: string, value: string | number) => (
		<div className="row">
			<div className="icon">{icon}</div>
			<div className="detailsContainer">
				<div className="details">
					<Text title={key} type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
					<Text title={value} type="psm" fontWeight="semiBold" />
				</div>
			</div>
		</div>
	);

	return (
		<div className="behaviorReductionDetails">
			<div className="rowContainer">
				{renderRow(<BrainIcon />, 'Behavior name', name)}
				{renderRow(<BullsEyeArrowIcon />, 'Goal name', goalName || 'No goal name')}
				{renderRow(<GearIcon />, 'Behavior definition', definition || 'No definition')}
				{renderRow(<GearIcon />, 'Category', category.name)}
				{renderRow(<ClapperBoardIcon />, 'Recoding type', recordingType.text)}
				{renderRow(<CalendarDayIcon />, 'Date created', dayjs(createdAt).format('MM/DD/YYYY'))}
				{renderRow(<DatabaseIcon />, 'Data today', dataToday)}
				{renderRow(<CircleIcon />, 'Status', isArchived ? 'Archived' : 'Active')}
				<div className="behaviorReductionDetails">
					<EditBehaviorDetails data={{ ...data, clientId, doNotZeroOut: false }} />
					<DeleteBehavior data={{ clientId, id }} />
					{isArchived ? <UnarchiveComponent data={{ clientId, id }} /> : <ArchiveComponent data={{ clientId, id }} />}
					<GraphBehavior data={data} behaviorId={id} />
				</div>
			</div>
		</div>
	);
};

const Details = ({ data, allBehaviors, currentIndex = 0 }: DetailsProps) => {
	const [open, setOpen] = useState<boolean>(false);
	const [activeIndex, setActiveIndex] = useState<number>(currentIndex);

	const hasNavigation = allBehaviors && allBehaviors.length > 1;
	const currentBehavior = hasNavigation ? allBehaviors[activeIndex] : data;

	const handlePrevious = () => {
		if (activeIndex > 0) {
			setActiveIndex(activeIndex - 1);
		}
	};

	const handleNext = () => {
		if (allBehaviors && activeIndex < allBehaviors.length - 1) {
			setActiveIndex(activeIndex + 1);
		}
	};

	const handleClose = () => {
		setOpen(false);
		// Reset to initial index when closing
		setActiveIndex(currentIndex);
	};

	const handleOpen = () => {
		setActiveIndex(currentIndex); // Reset to current item when opening
		setOpen(true);
	};

	return (
		<>
			<Tooltip title="More Information">
				<div className="behaviorReductionInfoIcon" onClick={handleOpen}>
					<InfoSvg />
				</div>
			</Tooltip>
			<CustomAlert
				title="Behavior details"
				titleOnTop={false}
				open={open}
				onClose={handleClose}
				Content={() => (
					<>
						<DetailsContent data={currentBehavior} />
						{hasNavigation && (
							<PopupNavigation
								currentIndex={activeIndex}
								totalCount={allBehaviors.length}
								onPrevious={handlePrevious}
								onNext={handleNext}
							/>
						)}
					</>
				)}
				Icon={BrainIcon}
			/>
		</>
	);
};

export default Details;
